import {
  MessageSquare,
  MousePointerClick,
  ShoppingCart,
  DollarSign,
  Mail,
  Bell
} from 'lucide-react';
import { MetricsCard } from './metrics-card';

interface ChannelBreakdown {
  sms: number;
  email: number;
  push: number;
}

interface MetricsSectionProps {
  messagesSent: number;
  unsubscribed: number;
  clicks: number;
  ctr: number;
  orders: number;
  cvr: number;
  revenue: number;
  epm: number;
  channelBreakdowns: {
    messages: ChannelBreakdown;
    clicks: ChannelBreakdown;
    orders: ChannelBreakdown;
    revenue: ChannelBreakdown;
  };
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(num >= 100000 ? 0 : 1)}k`;
  return num.toString();
}

function formatCurrency(amount: number): string {
  if (amount >= 1000) {
    return `$${formatNumber(amount)}`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }).format(amount);
}

function formatPercentage(value: number): string {
  return `${value.toFixed(2)}%`;
}

function ChannelBreakdownTooltip({
  breakdown,
  title = 'Clicks'
}: {
  breakdown: ChannelBreakdown;
  title?: string;
}) {
  return (
    <div className="w-full p-4 bg-card rounded-lg">
      <h4 className="mb-6 text-xl font-medium">{title}</h4>
      <div className="flex justify-between">
        <div className="flex flex-col items-center">
          <MessageSquare className="h-5 w-5 text-muted-foreground mb-2" />
          <div className="text-4xl font-medium tabular-nums">{formatNumber(breakdown.sms)}</div>
          <span className="text-sm text-muted-foreground mt-1">SMS</span>
        </div>
        <div className="h-20 w-px bg-border"></div>
        <div className="flex flex-col items-center">
          <Bell className="h-5 w-5 text-muted-foreground mb-2" />
          <div className="text-4xl font-medium tabular-nums">{formatNumber(breakdown.push)}</div>
          <span className="text-sm text-muted-foreground mt-1">Push</span>
        </div>
        <div className="h-20 w-px bg-border"></div>
        <div className="flex flex-col items-center">
          <Mail className="h-5 w-5 text-muted-foreground mb-2" />
          <div className="text-4xl font-medium tabular-nums">{formatNumber(breakdown.email)}</div>
          <span className="text-sm text-muted-foreground mt-1">Email</span>
        </div>
      </div>
    </div>
  );
}

export function MetricsSection({
  messagesSent,
  unsubscribed,
  clicks,
  ctr,
  orders,
  cvr,
  revenue,
  epm,
  channelBreakdowns
}: MetricsSectionProps) {
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      <MetricsCard
        title="Messages Sent"
        value={formatNumber(messagesSent)}
        subtitle={`${formatNumber(unsubscribed)} Unsubscribed`}
        icon={<MessageSquare className="h-5 w-5" />}
        tooltipContent={
          <ChannelBreakdownTooltip title="Messages Sent" breakdown={channelBreakdowns.messages} />
        }
      />
      <MetricsCard
        title="Clicks"
        value={formatNumber(clicks)}
        subtitle={`${formatPercentage(ctr)} CTR`}
        icon={<MousePointerClick className="h-5 w-5" />}
        tooltipContent={
          <ChannelBreakdownTooltip title="Clicks" breakdown={channelBreakdowns.clicks} />
        }
      />
      <MetricsCard
        title="Total Orders"
        value={formatNumber(orders)}
        subtitle={`${formatPercentage(cvr)} CVR`}
        icon={<ShoppingCart className="h-5 w-5" />}
        tooltipContent={
          <ChannelBreakdownTooltip title="Total Orders" breakdown={channelBreakdowns.orders} />
        }
      />
      <MetricsCard
        title="Earned Revenue"
        value={formatCurrency(revenue)}
        subtitle={`$${formatNumber(epm)} EPM`}
        icon={<DollarSign className="h-5 w-5" />}
        tooltipContent={
          <ChannelBreakdownTooltip title="Earned Revenue" breakdown={channelBreakdowns.revenue} />
        }
      />
    </div>
  );
}
